import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  IconStar,
  IconLink,
} from 'src/components/Icons';

import { InitialSetup } from './Setup Components/InitialSetup';
import { DeckNavigation } from './Setup Components/DeckNavigation';
import { EnableDisableDecks } from './Setup Components/EnableDisableDecks';
import { HideDecks } from './Setup Components/HideDecks';
import { StopResetButtons } from './Setup Components/StopResetButtons';
import { FloatingHopperButton } from './Setup Components/FloatingHopperButton';
import { CustomButtonText } from './Setup Components/CustomButtonText';
import { TwitchEmbeds } from './Setup Components/TwitchEmbeds';
import { PanelWaitForCommands } from './Setup Components/PanelWaitForCommands';
import { LaunchFlags } from './Setup Components/LaunchFlags';
import { ProxyButtons } from './Setup Components/ProxyButtons';
import { Settings } from './Setup Components/Settings';
import { VibrateDevice } from './Setup Components/VibrateDevice';
import { RealTimeInspector } from './Setup Components/RealTimeInspector';
import { PopupMessage } from './Setup Components/PopupMessage';
import { ShowUrl } from './Setup Components/ShowUrl';
import { DeckHopperServer } from './Setup Components/DeckHopperServer';
import { DeckInspector } from './Setup Components/DeckInspector';
import { RecordButtonActivity } from './Setup Components/RecordButtonActivity';

type SectionKey =
  | 'initialSetup'
  | 'navigation'
  | 'enableDisableDecks'
  | 'stopResetButtons'
  | 'floatingHopperButton'
  | 'customButtonText'
  | 'settings'
  | 'panelWaitForCommands'
  | 'launchFlags'
  | 'realTimeInspector'
  | 'vibrateDevice'
  | 'twitchEmbeds'
  | 'proxyButtons'
  | 'hideDecks'
  | 'popupMessage'
  | 'showUrl'
  | 'deckHopperServer'
  | 'deckInspector'
  | 'recordButtonActivity';

const Setup: React.FC = () => {
  /* ---------- open state ---------- */
  const [openSections, setOpenSections] = useState<
    Record<SectionKey, boolean>
  >({
    initialSetup: false,
    navigation: false,
    enableDisableDecks: false,
    stopResetButtons: false,
    floatingHopperButton: false,
    customButtonText: false,
    settings: false,
    panelWaitForCommands: false,
    launchFlags: false,
    vibrateDevice: false,
    twitchEmbeds: false,
    proxyButtons: false,
    hideDecks: false,
    realTimeInspector: false,
    popupMessage: false,
    showUrl: false,
    deckHopperServer: false,
    deckInspector: false,
    recordButtonActivity: false,
  });

  /* ---------- refs for scrolling ---------- */
  const sectionRefs = useRef<Record<SectionKey, HTMLElement | null>>({
    initialSetup: null,
    navigation: null,
    enableDisableDecks: null,
    stopResetButtons: null,
    floatingHopperButton: null,
    customButtonText: null,
    settings: null,
    panelWaitForCommands: null,
    launchFlags: null,
    vibrateDevice: null,
    twitchEmbeds: null,
    proxyButtons: null,
    hideDecks: null,
    realTimeInspector: null,
    popupMessage: null,
    showUrl: null,
    deckHopperServer: null,
    deckInspector: null,
    recordButtonActivity: null,
  });

  /* ---------- helper: open + scroll ---------- */
  const openAndScroll = (key: SectionKey) => {
    setOpenSections((prev) => {
      const newState: Record<SectionKey, boolean> = {} as any;
      (Object.keys(prev) as SectionKey[]).forEach(
        (k) => (newState[k] = k === key ? !prev[k] : false)
      );
      return newState;
    });

    // wait a tick so the section renders open, then scroll
    setTimeout(() => {
      const el = sectionRefs.current[key];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  /* ---------- open via URL hash on load ---------- */
  useEffect(() => {
    const hash = window.location.hash.replace('#', '') as SectionKey;
    if (hash && hash in openSections) {
      openAndScroll(hash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once

  /* ---------- expandable row component ---------- */
  interface ExpandableSectionProps {
    title: string;
    Component: React.FC<any>;
    anchor: SectionKey;
    isPro?: boolean;
    componentProps?: Record<string, any>;
  }

  const ExpandableSection: React.FC<
    ExpandableSectionProps & { isOpen: boolean }
  > = ({ title, Component, isPro, anchor, isOpen, componentProps }) => {
    const copyAnchor = async () => {
      const url = `${window.location.origin}${window.location.pathname}#${anchor}`;
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // fallback for browsers without clipboard API
        const area = document.createElement('textarea');
        area.value = url;
        area.style.position = 'fixed';
        area.style.left = '-9999px';
        document.body.appendChild(area);
        area.select();
        document.execCommand('copy');
        document.body.removeChild(area);
      }
    };

    return (
      <div
        ref={(el) => (sectionRefs.current[anchor] = el)}
        className="scroll-mt-24 flex flex-col w-full my-2 p-1 md:mx-3 border-b rounded-lg border-gray-600/50"
      >
        <button
          className={`rounded-lg flex justify-between items-center w-full text-left gap-5 ${isOpen ? 'bg-blue-600/50' : ''
            } text-gray-300 p-3 hover:bg-blue-600/50`}
          onClick={() => openAndScroll(anchor)}
        >
          <div className="flex items-center gap-2">
            <h2 className="text-lg">
              {title} {isPro && <IconStar />}
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            <span
              onClick={(e) => {
                e.stopPropagation();
                copyAnchor();
              }}
              title="Copy link"
              className="text-gray-400 hover:text-gray-200"
            >
              <IconLink className="w-5 h-5" />
            </span>
            {isOpen ? (
              <ChevronUpIcon className="w-5 h-5" />
            ) : (
              <ChevronDownIcon className="w-5 h-5" />
            )}
          </div>
        </button>

        {isOpen && (
          <div className="rounded-lg text-gray-300 py-6 px-4 bg-bgBorderBlue bg-opacity-30">
            <Component {...(componentProps || {})} />
          </div>
        )}
      </div>
    );
  };

  /* ---------- render ---------- */
  return (
    <div className="flex flex-col items-center bg-dark-blue text-gray-300 p-4 w-full mx-auto pb-10">
      <h1 className="text-3xl md:text-4xl text-center text-white mt-4 mb-4">
        Setup
      </h1>
      <p className="text-lg md:text-xl text-center text-gray-400 mb-6">
        Learn how to set up Deck Hopper and use all its features.
      </p>
      <hr className="border-t border-gray-600 w-2/3 mb-8" />

      <div className="flex flex-col w-full max-w-screen-lg">
        <ExpandableSection
          title="Initial Setup"
          Component={InitialSetup}
          isOpen={openSections.initialSetup}
          anchor="initialSetup"
        />

        <ExpandableSection
          title="Deck Hopper in a browser or OBS Dock"
          Component={DeckHopperServer}
          isOpen={openSections.deckHopperServer}
          isPro
          anchor="deckHopperServer"
        />

        <ExpandableSection
          title="Navigating Between Decks"
          Component={DeckNavigation}
          isOpen={openSections.navigation}
          anchor="navigation"
        />

        <ExpandableSection
          title="Changing Deck Status"
          Component={EnableDisableDecks}
          isOpen={openSections.enableDisableDecks}
          isPro
          anchor="enableDisableDecks"
        />

        <ExpandableSection
          title="Hiding Decks From View"
          Component={HideDecks}
          isOpen={openSections.hideDecks}
          isPro
          anchor="hideDecks"
        />
        <ExpandableSection
          title="Advanced Button Menu"
          Component={StopResetButtons}
          isOpen={openSections.stopResetButtons}
          isPro
          anchor="stopResetButtons"
          componentProps={{ isMobileView: false }}
        />


        <ExpandableSection
          title="Floating Hopper Button"
          Component={FloatingHopperButton}
          isOpen={openSections.floatingHopperButton}
          anchor="floatingHopperButton"
        />

        <ExpandableSection
          title='Deck Inspector'
          Component={DeckInspector}
          isOpen={openSections.deckInspector}
          anchor="deckInspector"
        />


        <ExpandableSection
          title="Real Time Inspector"
          Component={RealTimeInspector}
          isOpen={openSections.realTimeInspector}
          isPro
          anchor="realTimeInspector"
        />

        <ExpandableSection
          title="Record Button Activity"
          Component={RecordButtonActivity}
          isOpen={openSections.recordButtonActivity}
          isPro
          anchor="recordButtonActivity"
        />


        <ExpandableSection
          title="Proxy Buttons"
          Component={ProxyButtons}
          isOpen={openSections.proxyButtons}
          isPro
          anchor="proxyButtons"
        />

        <ExpandableSection
          title="Twitch Embeds"
          Component={TwitchEmbeds}
          isOpen={openSections.twitchEmbeds}
          isPro
          anchor="twitchEmbeds"
        />

        <ExpandableSection
          title="Deck App: Wait For Commands"
          Component={PanelWaitForCommands}
          isOpen={openSections.panelWaitForCommands}
          isPro
          anchor="panelWaitForCommands"
        />

        <ExpandableSection
          title="Show and Close Popup Message"
          Component={PopupMessage}
          isOpen={openSections.popupMessage}
          isPro
          anchor="popupMessage"
        />

        <ExpandableSection
          title="Show and Close URL"
          Component={ShowUrl}
          isOpen={openSections.showUrl}
          isPro
          anchor="showUrl"
        />

        <ExpandableSection
          title="Vibrate Device"
          Component={VibrateDevice}
          isOpen={openSections.vibrateDevice}
          anchor="vibrateDevice"
        />

        <ExpandableSection
          title="Customize Button Text"
          Component={CustomButtonText}
          isOpen={openSections.customButtonText}
          isPro
          anchor="customButtonText"
        />

        <ExpandableSection
          title="Launch Flags"
          Component={LaunchFlags}
          isOpen={openSections.launchFlags}
          isPro
          anchor="launchFlags"
        />

        <ExpandableSection
          title="Settings"
          Component={Settings}
          isOpen={openSections.settings}
          anchor="settings"
        />
      </div>
    </div>
  );
};

export default Setup;
